// Admin: who can be paid directly (contributors with validated tests,
// moderators at month end), direct payouts, and the paginated lists of the
// moderators and contributors tabs.
import { useMemo, useState } from "react";
import { payoutsApi } from "@/api/payouts.api";
import { testsApi } from "@/api/tests.api";
import type { useConfirm } from "@/hooks/use-confirm";
import type { BackendPayoutRequest, ModeratorForPayout, UserForPayout } from "@/lib/types/payments";
import { formatCurrency, hasBeenPaidThisMonth, paginateItems } from "../helpers";
import { aggregateEligibleUsers, apiErrorMessage, parseNetworkInput } from "../logic/payout-logic";
import { isAdminOrSuper, type GetPaidBase, type MoneyFormat } from "./get-paid-context";

interface PayoutCandidatesInput extends GetPaidBase, MoneyFormat {
  confirmDialog: ReturnType<typeof useConfirm>;
  monthClosed: boolean;
  contributorAmount: number;
  moderatorAmount: number;
  setLoadingTests: (loading: boolean) => void;
  loadBudgetAndStats: () => Promise<void>;
  moderatorRequestsToProcess: BackendPayoutRequest[];
  contributorRequestsToProcess: BackendPayoutRequest[];
}

export function usePayoutCandidates(input: PayoutCandidatesInput) {
  const { t, user, toast, locale, currency, confirmDialog, monthClosed, contributorAmount, moderatorAmount, setLoadingTests, loadBudgetAndStats, moderatorRequestsToProcess, contributorRequestsToProcess } = input;
  const [usersForPayout, setUsersForPayout] = useState<UserForPayout[]>([]);
  const [moderatorsForPayout, setModeratorsForPayout] = useState<ModeratorForPayout[]>([]);
  const [moderatorRequestsPage, setModeratorRequestsPage] = useState(1);
  const [moderatorInfoPage, setModeratorInfoPage] = useState(1);
  const [contributorRequestsPage, setContributorRequestsPage] = useState(1);
  const [contributorInfoPage, setContributorInfoPage] = useState(1);

  const loadEligibleUsers = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    setLoadingTests(true);
    try {
      const res = await testsApi.getValidatedSubmissions(1, 100);
      const items = Array.isArray(res?.items) ? res.items : [];
      const eligibleUsers = aggregateEligibleUsers(items, contributorAmount);
      const usersWithAccumulatedAmount = await Promise.all(
        eligibleUsers.map(async (eligibleUser) => {
          try {
            const accumulated = await payoutsApi.getContributorAccumulatedAmount(eligibleUser.id);
            return { ...eligibleUser, amount: accumulated.amount > 0 ? accumulated.amount : eligibleUser.amount };
          } catch {
            return eligibleUser;
          }
        }),
      );
      setUsersForPayout(usersWithAccumulatedAmount.sort((a, b) => b.score - a.score));
    } catch {
      toast({ title: t.error, description: t.getPaid.eligibleUsersLoadError, variant: "destructive" });
    } finally {
      setLoadingTests(false);
    }
  };

  const loadEligibleModerators = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    try {
      const res = await payoutsApi.getEligibleModerators();
      const mods = await Promise.all(
        res.moderators.map(async (m: any) => {
          try {
            const accumulated = await payoutsApi.getModeratorAccumulatedAmount(m.id);
            return { ...m, lastPayoutDate: accumulated.lastPayoutDate || m.lastPayoutDate, earnedAmount: accumulated.amount };
          } catch {
            const alreadyPaidThisMonth = hasBeenPaidThisMonth(m.lastPayoutDate);
            return { ...m, earnedAmount: monthClosed && !alreadyPaidThisMonth ? moderatorAmount : 0 };
          }
        }),
      );
      setModeratorsForPayout(mods);
    } catch {
      toast({ title: t.error, description: t.getPaid.eligibleModeratorsLoadError, variant: "destructive" });
    }
  };

  /** Phone from the profile or a prompt, then the network; null when either is missing. */
  const askPayoutTarget = (phoneNumber: string | undefined, name: string) => {
    const phone = phoneNumber || window.prompt(t.getPaid.phonePrompt.replace("{name}", name), "");
    if (!phone) {
      toast({ title: t.getPaid.phoneRequiredTitle, description: t.getPaid.phoneMissing, variant: "destructive" });
      return null;
    }
    const network = parseNetworkInput(window.prompt(t.getPaid.networkPrompt, "FLOOZ"));
    if (!network) {
      toast({ title: t.getPaid.invalidNetwork, description: t.getPaid.chooseNetwork, variant: "destructive" });
      return null;
    }
    return { phone, network };
  };

  const handlePayUser = async (userPayment: UserForPayout) => {
    if (!isAdminOrSuper(user?.role)) return;
    const target = askPayoutTarget(userPayment.phoneNumber, userPayment.username);
    if (!target) return;
    try {
      const res = await payoutsApi.payoutUser({
        userId: Number(userPayment.id),
        phone: target.phone,
        amount: Math.round(Number(userPayment.amount)),
        network: target.network,
        description: t.getPaid.payoutDescription.replace("{count}", String(userPayment.validatedTests)),
      });
      toast({
        title: t.getPaid.paymentLaunched,
        description: `${formatCurrency(userPayment.amount, locale, currency)} vers ${userPayment.username} • ${t.getPaid.paymentReference}: ${res.tx_reference}`,
      });
      setUsersForPayout((prev) => prev.filter((u) => u.id !== userPayment.id));
      loadBudgetAndStats();
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.paymentLaunchFailed), variant: "destructive" });
    }
  };

  const handlePayModerator = async (mod: ModeratorForPayout) => {
    if (!isAdminOrSuper(user?.role)) return;
    const target = askPayoutTarget(mod.phoneNumber, mod.name);
    if (!target) return;
    try {
      const res = await payoutsApi.payoutModerator({
        moderatorId: mod.id,
        phone: target.phone,
        amount: Math.round(mod.earnedAmount),
        network: target.network,
        description: t.getPaid.moderatorMonthlyPayout,
      });
      toast({
        title: t.getPaid.paymentLaunched,
        description: `${formatCurrency(mod.earnedAmount, locale, currency)} vers ${mod.name} • ${t.getPaid.paymentReference}: ${res.tx_reference}`,
      });
      setModeratorsForPayout((prev) => prev.filter((m) => m.id !== mod.id));
      loadBudgetAndStats();
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.paymentLaunchFailed), variant: "destructive" });
    }
  };

  const handlePayAllModerators = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    const total = moderatorsForPayout.reduce((sum, m) => sum + m.earnedAmount, 0);
    const [question, amountLine] = t.getPaid.payModeratorsConfirm
      .replace("{count}", String(moderatorsForPayout.length))
      .replace("{amount}", formatCurrency(total, locale, currency))
      .split("\n");
    const confirmed = await confirmDialog({ title: question, description: amountLine, tone: "danger" });
    if (!confirmed) return;

    try {
      const res = await payoutsApi.payAllModerators();
      toast({
        title: t.getPaid.paymentsLaunched,
        description: t.getPaid.paymentsLaunchedDesc
          .replace("{success}", String(res.success))
          .replace("{failed}", String(res.failed))
          .replace("{total}", String(res.total)),
      });
      loadEligibleModerators();
      loadBudgetAndStats();
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.launchPaymentsFailed), variant: "destructive" });
    }
  };

  const moderatorsWithoutRequest = useMemo(() => {
    const withRequest = new Set(moderatorRequestsToProcess.map((request) => String(request.userId)));
    return moderatorsForPayout.filter((moderator) => !withRequest.has(String(moderator.id)));
  }, [moderatorsForPayout, moderatorRequestsToProcess]);

  const contributorsWithoutRequest = useMemo(() => {
    const withRequest = new Set(contributorRequestsToProcess.map((request) => String(request.userId)));
    return usersForPayout.filter((contributor) => !withRequest.has(String(contributor.id)));
  }, [usersForPayout, contributorRequestsToProcess]);

  return {
    usersForPayout, moderatorsForPayout,
    loadEligibleUsers, loadEligibleModerators,
    handlePayUser, handlePayModerator, handlePayAllModerators,
    moderatorsWithoutRequest, contributorsWithoutRequest,
    moderatorRequestsPage, setModeratorRequestsPage,
    moderatorInfoPage, setModeratorInfoPage,
    contributorRequestsPage, setContributorRequestsPage,
    contributorInfoPage, setContributorInfoPage,
    paginatedModeratorRequests: paginateItems(moderatorRequestsToProcess, moderatorRequestsPage),
    paginatedModeratorInfo: paginateItems(moderatorsWithoutRequest, moderatorInfoPage),
    paginatedContributorRequests: paginateItems(contributorRequestsToProcess, contributorRequestsPage),
    paginatedContributorInfo: paginateItems(contributorsWithoutRequest, contributorInfoPage),
  };
}
